// components/transferts/TransfertItem.tsx
import React from "react";
import {
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Ban,
  Building2,
  DollarSign,
  Banknote,
  ArrowRight,
  Key,
  Shield,
  Calendar,
  Landmark,
  AlertCircle,
} from "lucide-react";
import { BankTransfertItem } from "../../../../types/features/administration/VirementBancaire";

interface TransfertItemProps {
  transfertItem: BankTransfertItem;
  onViewDetails: (item: BankTransfertItem) => void;
  onCheckStatus: (item: BankTransfertItem) => void;
  onChangeStatus: (item: BankTransfertItem) => void;
  onVerifyKey: (item: BankTransfertItem) => void;
  onComplete: (item: BankTransfertItem) => void;
  onInProgress: (item: BankTransfertItem) => void;
  onFail: (item: BankTransfertItem) => void;
  onCancel: (item: BankTransfertItem) => void;
  loadingStates?: {
    complete?: string;
    inProgress?: string;
    fail?: string;
    cancel?: string;
  };
}

const TransfertItem: React.FC<TransfertItemProps> = ({
  transfertItem,
  onViewDetails,
  onCheckStatus,
  onChangeStatus,
  onVerifyKey,
  onComplete,
  onInProgress,
  onFail,
  onCancel,
  loadingStates = {},
}) => {
  const { transfert, details } = transfertItem;

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, any> = {
      COMPLETED: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Complété", icon: CheckCircle },
      SUCCEEDED: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Réussi", icon: CheckCircle },
      PENDING: { bg: "bg-blue-100", text: "text-blue-700", label: "En Attente", icon: Clock },
      FAILED: { bg: "bg-red-100", text: "text-red-700", label: "Échoué", icon: XCircle },
      CANCELLED: { bg: "bg-gray-100", text: "text-gray-700", label: "Annulé", icon: Ban },
      CANCELED: { bg: "bg-gray-100", text: "text-gray-700", label: "Annulé", icon: Ban },
      PROCESSING: { bg: "bg-amber-100", text: "text-amber-700", label: "En traitement", icon: RefreshCw },
      IN_PROGRESS: { bg: "bg-amber-100", text: "text-amber-700", label: "En cours", icon: RefreshCw },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}
      >
        <Icon className="w-3 h-3" /> {config.label}
      </span>
    );
  };

  const getPayoutBadge = (payoutStatus: string | null) => {
    if (!payoutStatus)
      return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">En attente</span>;
    if (payoutStatus === "PENDING_MANUAL_PAYOUT")
      return <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700">⚠️ Paiement manuel</span>;
    if (payoutStatus === "COMPLETED")
      return <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">✓ Distribué</span>;
    if (payoutStatus === "FAILED")
      return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">✗ Échec</span>;
    return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">{payoutStatus}</span>;
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  const formatAmount = (amount: number, currency: string) =>
    `${amount.toLocaleString()} ${currency === "CAD" ? "CAD" : "FCFA"}`;

  const isLoadingComplete = loadingStates.complete === transfert.id;
  const isLoadingInProgress = loadingStates.inProgress === transfert.id;
  const isLoadingFail = loadingStates.fail === transfert.id;
  const isLoadingCancel = loadingStates.cancel === transfert.id;

  return (
    <div className="mb-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="grid grid-cols-12 gap-4 items-center">
          {/* Référence & Date */}
          <div className="col-span-2">
            <p className="font-mono font-semibold text-gray-900 text-sm">{transfert.txt_reference}</p>
            <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(transfert.created_at)}
            </p>
          </div>

          {/* Bénéficiaire */}
          <div className="col-span-2">
            <p className="text-sm font-semibold text-gray-900 truncate">{details.bank_account_owner}</p>
            <p className="text-xs text-gray-500">{details.bank_name}</p>
          </div>

          {/* Montants */}
          <div className="col-span-2">
            <p className="text-md font-bold text-gray-900 flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              {formatAmount(transfert.sending_amount, "CAD")}
            </p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" />
              <Banknote className="w-3 h-3" />
              {formatAmount(transfert.receiving_amount, "XAF")}
            </p>
            {transfert.fee > 0 && <p className="text-xs text-red-500">Frais: {formatAmount(transfert.fee, "CAD")}</p>}
          </div>

          {/* Infos bancaires */}
          <div className="col-span-2">
            <p className="text-xs text-gray-500 font-mono">{details.account_number}</p>
            <p className="text-xs text-gray-400">Clé: {details.security_key}</p>
          </div>

          {/* Statuts */}
          <div className="flex flex-col col-span-1 gap-1">
            {getStatusBadge(transfert.status)}
            {getPayoutBadge(details.status)}
          </div>

          {/* Actions */}
          {/* Actions - version corrigée sans redondance */}
          <div className="flex flex-wrap gap-1 col-span-2">
            <button
              onClick={() => onViewDetails(transfertItem)}
              className="p-1.5 bg-blue-50 hover:bg-blue-100 rounded"
              title="Détails"
            >
              <Eye className="w-3.5 h-3.5 text-blue-600" />
            </button>
            <button
              onClick={() => onCheckStatus(transfertItem)}
              className="p-1.5 bg-purple-50 hover:bg-purple-100 rounded"
              title="Statut"
            >
              <RefreshCw className="w-3.5 h-3.5 text-purple-600" />
            </button>
            <button
              onClick={() => onVerifyKey(transfertItem)}
              className="p-1.5 bg-indigo-50 hover:bg-indigo-100 rounded"
              title="Vérifier clé"
            >
              <Key className="w-3.5 h-3.5 text-indigo-600" />
            </button>
            <button
              onClick={() => onChangeStatus(transfertItem)}
              className="p-1.5 bg-slate-50 hover:bg-slate-100 rounded"
              title="Changer statut"
            >
              <Shield className="w-3.5 h-3.5 text-slate-600" />
            </button>

            {/* Actions de modification - conditions simplifiées */}
            {(transfert.status === "PENDING" || details.status === "PENDING_MANUAL_PAYOUT") && (
              <button
                onClick={() => onComplete(transfertItem)}
                disabled={isLoadingComplete}
                className="p-1.5 bg-green-50 hover:bg-green-100 rounded disabled:opacity-50"
              >
                {isLoadingComplete ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                )}
              </button>
            )}

            {transfert.status === "PENDING" && (
              <button
                onClick={() => onInProgress(transfertItem)}
                disabled={isLoadingInProgress}
                className="p-1.5 bg-amber-50 hover:bg-amber-100 rounded disabled:opacity-50"
              >
                {isLoadingInProgress ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 text-amber-600" />
                )}
              </button>
            )}

            {transfert.status !== "COMPLETED" && transfert.status !== "CANCELLED" && (
              <>
                <button
                  onClick={() => onFail(transfertItem)}
                  disabled={isLoadingFail}
                  className="p-1.5 bg-red-50 hover:bg-red-100 rounded disabled:opacity-50"
                >
                  {isLoadingFail ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-red-600" />
                  )}
                </button>
                <button
                  onClick={() => onCancel(transfertItem)}
                  disabled={isLoadingCancel}
                  className="p-1.5 bg-gray-50 hover:bg-gray-200 rounded disabled:opacity-50"
                >
                  {isLoadingCancel ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Ban className="w-3.5 h-3.5 text-gray-600" />
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransfertItem;
